import TableLanguagesCell from '@/contexts/shared/components/table/TableLanguagesCell'
import { DataGrid, GridActionsCellItem, GridCellParams, GridColDef, GridRowParams } from '@mui/x-data-grid'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import useScreenWidth from '../../../../../shared/hooks/useScreenWidth'
import { Slider } from '../../../models/sliders'
import SlidersCustomToolbar from './SlidersCustomToolbar'
import SlidersTitleCell from './SlidersTitleCell'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ViewIcon from '@mui/icons-material/Visibility'

interface SlidersTableProps {
  sliders: Slider[]
  loading: boolean
  onView: (id: number) => void
  onEdit: (id: number) => void
  onDelete: (id: number) => void
}

export const SlidersTable = ({ sliders, loading, onView, onEdit, onDelete }: SlidersTableProps) => {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)

  const getColumns = (): GridColDef[] => {
    return [
      {
        field: 'title',
        headerName: t('title'),
        flex: 1,
        renderCell: (params: GridCellParams) => <SlidersTitleCell params={params} />
      },
      {
        field: 'order',
        headerName: t('position'),
        align: 'center' as 'center',
        headerAlign: 'center' as 'center'
      },
      {
        field: 'brand.description',
        headerName: t('brand'),
        hideable: isMobile,
        valueGetter: (params: GridCellParams) => params.row.brand?.description || '-'
      },
      {
        field: 'languages',
        headerName: t('language'),
        renderCell: (params: GridCellParams) => <TableLanguagesCell params={params} />,
        hideable: isMobile
      },
      {
        field: 'role.name',
        headerName: t('rol'),
        hideable: isMobile,
        valueGetter: (params: GridCellParams) => params.row.role?.name || '-'
      },
      {
        field: 'date_ini',
        headerName: t('initial-date'),
        renderCell: (params: GridCellParams) => {
          if (params.row.date_ini != null) {
            return dayjs(params.row.date_ini).format('DD MMM YYYY')
          }
          return '--'
        },
        hideable: isMobile
      },
      {
        field: 'date_end',
        headerName: t('final-date'),
        renderCell: (params: GridCellParams) => {
          if (params.row.date_end != null) {
            return dayjs(params.row.date_end).format('DD MMM YYYY')
          }
          return '--'
        },
        hideable: isMobile
      },
      {
        field: 'actions',
        headerName: '',
        type: 'actions',
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            key={params.id}
            icon={<ViewIcon />}
            label={t('view')}
            onClick={() => onView(Number(params.id))}
            showInMenu
          />,
          <GridActionsCellItem
            key={params.id}
            icon={<EditIcon />}
            label={t('edit')}
            onClick={() => {
              onEdit(Number(params.id))
            }}
            showInMenu
          />,
          <GridActionsCellItem
            key={params.id}
            icon={<DeleteIcon />}
            label={t('delete')}
            onClick={() => {
              onDelete(Number(params.id))
            }}
            showInMenu
          />
        ]
      }
    ].filter(column => !(isMobile && ['brand.description', 'languages', 'role.name', 'date_ini', 'date_end'].includes(column.field)))
  }

  const columns = getColumns()

  return (
    <>
      <DataGrid
        rows={sliders}
        columns={columns}
        pageSizeOptions={[10, 20]}
        loading={loading}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 }
          }
        }}
        autoHeight
        density="comfortable"
        slots={{
          toolbar: SlidersCustomToolbar
        }}
        slotProps={{
          toolbar: { t }
        }}
        disableRowSelectionOnClick
      />
    </>
  )
}
